<template>
  <div :class="$style.sds">
    <aw-breadcrumb-version-2 />
    <aw-cms-white-card mobile-negative-margin :class="$style.card">
      <template #head>
        <lv-heading
          level="1"
          tag="h1"
          version2
        >
          {{ $awt('aw.sds_page.title') }}
        </lv-heading>
        <aw-separator />
        <ul :class="[$style.filters, 'ulReset']" :aria-label="$awt('aw.sds_page.wrapper_label')">
          <li v-for="filterOption in filterOptions" :key="filterOption">
            <button
              type="button"
              :aria-label="$awt('aw.sds_page.filter_button_label', {letter: filterOption})"
              :class="['buttonReset', $style.filter, {[$style.selected]: filterOption === selectedFilter}]"
              @click="selectFilter(filterOption)"
            >
              {{ filterOption.toUpperCase() }}
            </button>
          </li>
        </ul>
      </template>
      <div v-if="filteredSds.length > 0" :class="$style.list">
        <lv-button
          v-for="sds in filteredSds"
          :key="sds.id"
          :href="sds.url"
          target="_blank"
          shrink
          stretch
          :aria-label="$awt('aw.sds_page.option_label', {name: sds.name})"
          styling="link-primary-version2"
        >
          {{ sds.name }}
        </lv-button>
      </div>
      <aw-empty v-else>
        <span v-text="$awt('aw.sds_page.empty_message')" />
      </aw-empty>
    </aw-cms-white-card>
  </div>
</template>

<script setup lang="ts">

  import { defineI18nRoute } from '#i18n';
  import AwBreadcrumbVersion2 from '~~/common/components/Common/AwBreadcrumbVersion2.vue';
  import { useBreadcrumbStore } from '~~/common/stores/breadcrumb.js';
  import { useNuxtApp, useAsyncData } from 'nuxt/app';
  import AwCmsWhiteCard from '~~/common/components/AwCmsWhiteCard.vue';
  import { LvHeading, LvButton } from '~~/common/components/loginet-vue-shop/index';
  import AwSeparator from '~~/common/components/Common/AwSeparator.vue';
  import { computed, defineAsyncComponent, ref } from 'vue';
  import { setErrorNotification } from '~~/common/utils/helper';
  import slugify from 'slugify';
  import { AW_AX_CACHE_SEGMENTATION } from '~~/common/plugins/axios/awAxCache.js';
  import type { Schema } from '~~/common/types/api';

  type SdsDocument = Schema<'ProductDocumentListModel'>;

  const AwEmpty = defineAsyncComponent(() => import('~~/common/components/Common/AwEmpty.vue'));

  defineOptions({
    name: 'SdsPage',
  });

  // eslint-disable-next-line no-undef
  definePageMeta({
    layout: 'dynamic-content',
  });


  false && /*#__PURE__*/ defineI18nRoute({
    paths: {
      en: '/safety-data-sheet',
      hu: '/biztonsagi-adatlapok',
    },
  });

  const nuxtApp = useNuxtApp();
  const {
    $awt,
    $api,
    $logger,

  } = nuxtApp;
  const breadcrumbStore = useBreadcrumbStore();

  const filterOptions = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z'];
  const selectedFilter = ref<string | null>(null);
  const filteredSds = computed(() => {
    const filter = selectedFilter.value;
    return filter == null ? sdsPageContent.value : sdsPageContent.value.filter((sds) => slugify(sds.name, {
      lower: true,
      strict: true,
    }).startsWith(filter));
  });

  breadcrumbStore.setBreadcrumbs([
    {
      token: $awt('aw.common.category.home'),
      to: '/',
      disabled: false,
    },
    {
      token: $awt('aw.sds_page.title'),
      to: 'safety-data-sheet',
      disabled: true,
    },
  ]);


  const dataKey = 'sds_page_content';
  const { data: sdsPageContent } = await useAsyncData(
    dataKey,
    async () => {
      return await fetchSdsPageContent();
    },
    {
      default: () => [],
    },
  );

  async function fetchSdsPageContent () {
    try {
      return await $api.$get<SdsDocument[]>('/documents/SDS', { awAxCache: AW_AX_CACHE_SEGMENTATION });
    } catch (error) {
      $logger.error(error);
      setErrorNotification({
        nuxtApp,
        error,
      });
      return [];
    }
  }

  function selectFilter (filterOption: string) {
    if (filterOption === selectedFilter.value) {
      selectedFilter.value = null;
    } else {
      selectedFilter.value = filterOption;
    }
  }

</script>


<style module lang="scss" rel="stylesheet/scss">
.sds {
  .card {
    margin-top: 20px;
    margin-bottom: 20px;
    @include tablet(min) {
      margin-top: 40px;
      margin-bottom: 60px;
    }
  }
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;

  .filter {
    padding: 8px;
    border-radius: 6px;
    @include font($font-weight-medium-v2, 12px, 16px);
  }

  .selected {
    color: $color-text-inverse;
    background: $color-text-primary;
  }
}

.list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  @include tablet(min) {
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }
}
</style>
